/**
 * B1083 (M24 leg 1) — what an answer engine reads off a storefront page.
 *
 * ChatGPT, Gemini, Perplexity, Copilot and Meta Muse answer "what's on at
 * <venue> this weekend" from structured data, not from our React tree. Every
 * storefront page now carries ONE schema.org JSON-LD document: the landing
 * lists what the business sells; a picked card's page describes that one
 * thing with its prices and (for a SHOW) its showtimes. The words come from
 * the same catalog answer the page draws (B403 #12: the markup and the drawn
 * words say the same thing), so nothing here is a second source of truth.
 *
 * A SHOW tenant's product is a schema.org `Event` (the type Google's event
 * surfaces and the agents' discovery read); every other tenant type is a
 * `Product`/`Offer` pair. Prices are the card's display prices in major
 * units; availability is `InStock` because a card only reaches the storefront
 * while it sells (B973 drops unpriced types; retired products never arrive).
 *
 * Pure: no DOM, no fetch. `useJsonLd` (./useJsonLd.ts) puts the text in <head>.
 */

export interface JsonLdMoney {
  readonly amount: number;
  readonly currency: string;
}
export interface JsonLdVariant {
  readonly variantId: string;
  readonly name: string;
  readonly displayPrice?: JsonLdMoney | undefined;
}
export interface JsonLdCard {
  readonly productId: string;
  readonly name: string;
  readonly variants: readonly JsonLdVariant[];
  readonly presentation?:
    | {
        readonly summary?: string | undefined;
        readonly description?: string | undefined;
        readonly photos?: readonly { readonly url: string; readonly alt?: string | undefined }[] | undefined;
      }
    | undefined;
}
export interface JsonLdSlot {
  readonly slotId: string;
  readonly start: number;
  readonly from?: JsonLdMoney | undefined;
}
export interface JsonLdPage {
  /** The page's own URL — the agent's way back to us (tenant query included). */
  readonly pageUrl: string;
  readonly businessName?: string | undefined;
  /** The tenant's type word (SHOW, FERRY, ZOO, …); SHOW products are Events. */
  readonly tenantType?: string | undefined;
}

type Json = { readonly [key: string]: unknown };

/** Major units from the ledger's minor units — "31.90", never 3190. */
export function majorUnits(amount: number): string {
  return (amount / 100).toFixed(2);
}

function offers(card: JsonLdCard, pageUrl: string): Json[] {
  return card.variants
    .filter((v) => v.displayPrice !== undefined)
    .map((v) => ({
      "@type": "Offer",
      name: v.name,
      price: majorUnits(v.displayPrice!.amount),
      priceCurrency: v.displayPrice!.currency,
      availability: "https://schema.org/InStock",
      url: pageUrl,
    }));
}

function organizer(page: JsonLdPage): Json | undefined {
  return page.businessName === undefined || page.businessName === "" ? undefined : { "@type": "Organization", name: page.businessName };
}

function words(card: JsonLdCard): string | undefined {
  return card.presentation?.description ?? card.presentation?.summary;
}

function images(card: JsonLdCard): string[] {
  return (card.presentation?.photos ?? []).map((p) => p.url);
}

/** One card as the thing it is: an Event for a SHOW tenant, a Product otherwise. */
export function cardJsonLd(card: JsonLdCard, page: JsonLdPage, slots?: readonly JsonLdSlot[]): Json {
  const base: Json = {
    name: card.name,
    url: page.pageUrl,
    ...(words(card) !== undefined ? { description: words(card) } : {}),
    ...(images(card).length > 0 ? { image: images(card) } : {}),
    offers: offers(card, page.pageUrl),
  };
  if (page.tenantType === "SHOW") {
    const org = organizer(page);
    const sub = (slots ?? []).map((s) => ({
      "@type": "Event",
      name: card.name,
      startDate: new Date(s.start).toISOString(),
      url: page.pageUrl,
      ...(s.from !== undefined ? { offers: [{ "@type": "Offer", price: majorUnits(s.from.amount), priceCurrency: s.from.currency, availability: "https://schema.org/InStock", url: page.pageUrl }] } : {}),
    }));
    return {
      "@type": "Event",
      ...base,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      ...(org !== undefined ? { organizer: org, location: { "@type": "Place", name: page.businessName } } : {}),
      ...(sub.length > 0 ? { startDate: sub[0]!.startDate, subEvent: sub } : {}),
    };
  }
  const org = organizer(page);
  return { "@type": "Product", ...base, ...(org !== undefined ? { brand: org } : {}) };
}

/** The landing: the business and the list of what it sells. undefined until the catalog has answered. */
export function catalogJsonLd(cards: readonly JsonLdCard[], page: JsonLdPage, slotsByProduct?: ReadonlyMap<string, readonly JsonLdSlot[]>): Json | undefined {
  if (cards.length === 0) return undefined;
  const org = organizer(page);
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...(org !== undefined ? { name: page.businessName, provider: org } : {}),
    url: page.pageUrl,
    numberOfItems: cards.length,
    itemListElement: cards.map((card, i) => ({ "@type": "ListItem", position: i + 1, item: cardJsonLd(card, page, slotsByProduct?.get(card.productId)) })),
  };
}

/** A picked card's page: the one thing, with its showtimes when the calendar has answered. */
export function productJsonLd(card: JsonLdCard, page: JsonLdPage, slots?: readonly JsonLdSlot[]): Json {
  return { "@context": "https://schema.org", ...cardJsonLd(card, page, slots) };
}

/**
 * The script's text. `<` is escaped so a name like "<script>" can never close
 * the JSON-LD block — the one injection a head script is open to.
 */
export function jsonLdText(doc: Json): string {
  return JSON.stringify(doc).replace(/</g, "\\u003c");
}
